import AllRestaurants from './Components/AllRestaurants/AllRestaurants';
import Banner from './Components/Banner/Banner';
import Header from './Components/Header/Header';
import MyTopPick from './Components/MyTopPick/MyTopPick';
import RestaurantOfTheDay from './Components/RestaurantOfTheDay/RestaurantOfTheDay';
import TopCusines from './Components/TopCusines/TopCusines';
import RestaurantDetailPage from './Components/RestaurantDetailPage/RestaurantDetailPage';
import CheckoutPage from './Components/Checkout/CheckoutPage';
import AccountSettings from './Components/AccountSettings/AccountSettings';
import { BrowserRouter, Routes, Route, Outlet, useLocation, useNavigate } from 'react-router-dom';
import Footer from './Components/Footer/Footer';
import { ThemeProvider } from './theme/ThemeProvider';
import { defaultTheme } from './theme/defaultTheme';
import { AuthProvider } from './auth/AuthContext';
import { DeliveryAddressProvider } from './address/DeliveryAddressContext';
import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { useDeliveryAddress } from './address/DeliveryAddressContext';
import { LoadingProvider } from './loading/LoadingContext';
import { LoadingOverlay } from './Components/UI';
import GuestCartRoot from './guestCart/components/GuestCartRoot';
import { useSiteDocumentHead } from './seo/siteHead';

function Home(){
  const location = useLocation();
  const navigate = useNavigate();
  const { currentAddress, setCurrentAddress } = useDeliveryAddress();
  const [selectedCuisine, setSelectedCuisine] = useState(null);

  // Full load / remount of home: start at the top (banner). Remove legacy hash deep-links that skip the hero on refresh.
  useLayoutEffect(() => {
    if (window.location.hash === '#all-restaurants') {
      window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${window.location.search}`,
      );
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    if (!location.state?.scrollToAllRestaurants) return undefined;
    const t = window.setTimeout(() => {
      document.getElementById('all-restaurants')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      navigate('.', { replace: true, state: {} });
    }, 0);
    return () => window.clearTimeout(t);
  }, [location.state, navigate]);

  const handleLocationResolved = useCallback((loc) => {
    if (!loc) return;
    setCurrentAddress({
      address: loc.address || '',
      coords: loc.coords || (loc.latitude != null ? { latitude: loc.latitude, longitude: loc.longitude } : null),
      source: 'current',
    });
    setSelectedCuisine(null);
    window.setTimeout(() => {
      document.getElementById('all-restaurants')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  }, [setCurrentAddress]);

  const effectiveCoords = currentAddress?.coords || null;

  return (
    <div>
      <Banner onLocationResolved={handleLocationResolved} />
      <TopCusines
        selectedCuisineId={selectedCuisine?.id ?? null}
        onCuisineSelect={(c) => {
          setSelectedCuisine(c);
          if (c != null) {
            window.setTimeout(() => {
              document.getElementById('all-restaurants')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 0);
          }
        }}
      />
      <RestaurantOfTheDay />
      <MyTopPick />
      <AllRestaurants
        coordsOverride={effectiveCoords}
        cuisineId={selectedCuisine?.id ?? null}
        onClearCuisine={() => setSelectedCuisine(null)}
      />
    </div>
  );
}

function AppShell() {
  const { pathname } = useLocation();
  const headerWhite = pathname === '/' || pathname.startsWith('/restaurant') || pathname.startsWith('/checkout') || pathname.startsWith('/profile');

  useSiteDocumentHead();

  return (
    <div className="app-shell">
      <Header white={headerWhite} />
      <LoadingOverlay mode="nonBlocking" />
      <GuestCartRoot />
      <main className="app-main">
        <Outlet />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={defaultTheme}>
      <LoadingProvider>
        <AuthProvider>
          <DeliveryAddressProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<AppShell />}>
                <Route path="/" element={<Home/>} />
                <Route path="/restaurant/:slug" element={<RestaurantDetailPage/>} />
                <Route path="/checkout" element={<CheckoutPage />} />
                <Route path="/profile/account" element={<AccountSettings />} />
              </Route>
            </Routes>
          </BrowserRouter>
          </DeliveryAddressProvider>
        </AuthProvider>
      </LoadingProvider>
    </ThemeProvider>
  );
}

export default App;
